package com.steamstreet.vegasful.browser.components

import kotlinx.browser.document
import kotlinx.coroutines.*
import org.w3c.dom.HTMLTextAreaElement

/**
 * Starts a typewriter-like animation for the placeholders of an HTMLTextAreaElement.
 * The animation cycles through a list of hints, displaying each character progressively,
 * holding the hint for a specified duration, and then erasing it before moving to the next one.
 * The animation pauses if the user focuses on the textarea or starts typing.
 *
 * @param scope The coroutine scope in which the typewriter animation runs.
 * @param hints A list of strings to be displayed as placeholder text in the animation.
 * @param typingDelayMs The delay in milliseconds between typing each character (default is 45ms).
 * @param holdMs The duration in milliseconds to hold the fully typed placeholder text (default is 1200ms).
 * @param eraseDelayMs The delay in milliseconds between erasing each character (default is 28ms).
 * @param betweenMs The delay in milliseconds before starting the next hint after erasing (default is 400ms).
 * @return A Job representing the coroutine that manages the typewriter animation.
 *         This job can be canceled to stop the animation.
 */
fun HTMLTextAreaElement.typewriterPlaceholders(
    scope: CoroutineScope,
    hints: List<String>,
    typingDelayMs: Long = 45,
    holdMs: Long = 1200,
    eraseDelayMs: Long = 28,
    betweenMs: Long = 400
): Job = scope.launch {
    val el = this@typewriterPlaceholders
    while (isActive) {
        for (hint in hints) {
            // Skip typing while user is interacting
            if (el === document.activeElement || el.value.isNotBlank()) {
                // Clear placeholder and wait a bit before checking again
                el.placeholder = ""
                delay(300)
                continue
            }

            // Type
            el.placeholder = ""
            for (i in hint.indices) {
                // bail out if user starts typing/focuses mid-animation
                if (el === document.activeElement || el.value.isNotBlank()) {
                    el.placeholder = ""
                    break
                }
                el.placeholder = hint.substring(0, i + 1)
                delay(typingDelayMs)
            }

            // Hold
            if (el.placeholder.isNotEmpty()) delay(holdMs)

            // Erase
            while (el.placeholder.isNotEmpty()) {
                if (el === document.activeElement || el.value.isNotBlank()) {
                    el.placeholder = ""
                    break
                }
                el.placeholder = el.placeholder.dropLast(1)
                delay(eraseDelayMs)
            }

            delay(betweenMs)
        }
    }
}